// User-facing audio volumes. Persisted to localStorage and applied to
// every live AudioBus (menu + match) so a single slider drag updates both
// surfaces in lockstep. The bus exposes three GainNodes (master / music /
// sfx) sitting between every source and listener.getInput() — see
// GameAudio.ts. Per-source volumes (a map's natural music loudness, etc.)
// stay on the source; these knobs are the user's bus-level overlay.

import { CONFIG } from '../config';
import { AudioBus } from '../render/GameAudio';

export interface AudioSettings {
  master: number;
  music: number;
  sfx: number;
}

export const DEFAULTS: AudioSettings = {
  master: 1.0,
  music: 0.5,
  sfx: 1.0
};

const STORAGE_KEY = 'games:audioSettings';

export function loadAudioSettings(): AudioSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULTS };
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    return {
      master: clamp01(parsed.master, DEFAULTS.master),
      music:  clamp01(parsed.music,  DEFAULTS.music),
      sfx:    clamp01(parsed.sfx,    DEFAULTS.sfx)
    };
  } catch {
    return { ...DEFAULTS };
  }
}

export function saveAudioSettings(s: AudioSettings): void {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(s)); } catch { /* quota / private mode — ignore */ }
}

/** Apply the user's volumes to CONFIG.audio AND to every live AudioBus.
 *  CONFIG.audio is the seed for buses constructed AFTER this call (they
 *  read it in their constructor); the live walk catches any bus that was
 *  already constructed (e.g. the menu bus, which is built at module load). */
export function applyAudioSettings(s: AudioSettings): void {
  CONFIG.audio.masterVolume = s.master;
  CONFIG.audio.musicVolume  = s.music;
  CONFIG.audio.sfxVolume    = s.sfx;
  for (const bus of AudioBus.getLiveBuses()) {
    bus.setMasterVolume(s.master);
    bus.setMusicVolume(s.music);
    bus.setSfxVolume(s.sfx);
  }
}

function clamp01(v: unknown, fallback: number): number {
  if (typeof v !== 'number' || !Number.isFinite(v)) return fallback;
  return Math.max(0, Math.min(1, v));
}
